import { getDefaultStore } from 'jotai';
import { getUrlParam, getChannel, getSkuType } from '.';
import * as native from '@/utils/native';
import { QWebChannel } from '@/assets/js/qwebchannel';
import { fetchAuditVersionStatus } from '@/services/subscription';
import {
  auditVersionStatusAtom,
  curTabAtom,
  inviterUidAtom,
  isInitAtom,
  isViewNewUiAtom,
  rebateCodeAtom,
  userInfoAtom,
  vipInfoAtom,
  visibleExchangeAtom,
  apiDefaultTabAtom,
} from '@/store';
import { TrackData } from '@/page/subscription';
import {
  getGid,
  getIncomeSource,
  getTheme,
  getClientId,
  setToken,
  getTokenAndSet,
  closeVipSubscribeDialog,
} from './vip';
import { fetchUserToken, getRebateCode } from '@/services/user';
import { loadSubscriptionEntranceEntries, syncSubEntranceBizCode } from './subscriptionEntrance';
import { getAccountLoginUrl, getUserBatchSize, getUserInfo } from './user';
import { isTeamSubscription, SubscribeType, subscribeType, isOnlyShowScanUI } from '@/constants';
import { TabType, isProVipLevel } from '@/constants/subscription';
import initMtstat from './mtstat';
import {
  initAbTest,
  checkIsNewUI,
  applyMeidouRequireVipSceneOverride,
  applyProPeriodTabOnEnter,
} from './abtest';

const store = getDefaultStore();

/**
 * 获取服务端下发的是否需要勾选协议交互
 */
export const getAuditVersionStatus = async () => {
  const channel = getChannel();
  const version = await native.getVersion();
  const res = await fetchAuditVersionStatus({
    channel,
    version,
  });
  store.set(auditVersionStatusAtom, !!res?.data?.data?.status);
};

export const initQt = async () => {
  if (window.qt) {
    await new Promise<void>((rsolve) => {
      new QWebChannel(window.qt.webChannelTransport, (channel: any) => {
        window.qtObject = channel.objects.content;
        rsolve();
      });
    });
  }
};

/**
 * 埋点
 */
export const track = async (data: TrackData) => {
  // web端
  if (getIncomeSource() === 'MTXX-WEB') {
    if (!data.params) {
      data.params = {};
    }
    data.params.inviter = store.get(inviterUidAtom) || '';
    // 不能放在埋点初始化时因为curTabAtom是动态的
    data.params.sku_type = getSkuType();
    window._stat_track?.(data.event_id, data.params);
    return;
  }

  // PC端
  if (data.params) {
    const userInfo = store.get(userInfoAtom);
    const vipInfo = store.get(vipInfoAtom);
    const appVersion = await native.getVersion();
    // PC公共上报字段
    data.params.uid = `${userInfo?.id || ''}`;
    data.params.renew = (vipInfo && vipInfo.vip_end_time && !(vipInfo.vip_end_time < 0)) || false;
    data.params.channel = getChannel();
    data.params.entrance = getUrlParam('entrance') || '';

    data.params.app_version = appVersion || '';
    data.params.ab_info = decodeURIComponent(getUrlParam('ab_info') || '');

    // 透传权益相关
    const source = getUrlParam('source');
    const first_source = getUrlParam('first_source');
    const second_source = getUrlParam('second_source');
    const third_source = getUrlParam('third_source');
    const entrance_type = getUrlParam('entrance_type');
    const ID = getUrlParam('ID');
    if (source) data.params.source = source;
    if (first_source) data.params.first_source = first_source;
    if (second_source) data.params.second_source = second_source;
    if (third_source) data.params.third_source = third_source;
    if (entrance_type) data.params.entrance_type = entrance_type;
    if (ID) data.params.ID = ID;
  }

  native?.mtTrack(data);
};

/**
 * 新版订阅 UI 在同一页展示标准版+高级版卡片，初始化时不应落在 PRO_SUB（统一用 SUB 承载）。
 * 依赖 initAbTest 已完成，否则 checkIsNewUI 恒为 false；
 * 依赖 Promise.all 中的 loadSubscriptionEntranceEntries 已完成，否则 isFromChannelBusinessAtom 未更新、checkIsNewUI 对渠道用户判断不准。
 */
const resolveInitialCurTabForNewUi = (tab: TabType): TabType => {
  if (tab === TabType.PRO_SUB && checkIsNewUI()) {
    return TabType.SUB;
  }
  return tab;
};

const initCurTabValue = () => {
  const vipInfo = store.get(vipInfoAtom);
  const moduleId = decodeURIComponent(getUrlParam('module_id') || '');
  const source = decodeURIComponent(getUrlParam('source') || '');

  if (isOnlyShowScanUI) {
    // 从高级会员需求开始，废弃了URL透传default_tab来控制引导购买类型，改成全部直接在订阅弹窗内部自己控制
    // 只有在打开支付二维码弹窗时（isOnlyShowScanUI=1），才会接收使用default_tab
    const initDefaultTab = getUrlParam('default_tab');
    if (initDefaultTab) {
      store.set(curTabAtom, initDefaultTab as TabType);
      return;
    }
  }

  // 美豆高消耗项目[module_id, source]
  const hightConsumptionModules: string[][] = [
    ['roboneo_web', 'agent'], // agent编辑器
    ['product_kit_web', 'product_kit_web'], // AI商品套图
    ['product_kit_web', 'try_on'], // AI穿戴套图
    ['product_kit_web', 'A+_content'], // A+详情页
    ['product_kit_web', 'product_clone'], // 爆款图复刻
    ['product_shoot_web', 'product_shoot'], // AI商品图
    ['ai_commercial_web', 'aimodel'], // AI模特
    ['ai_commercial_web', 'ai_try_on_shoes'], // AI试鞋
    ['ailogo', 'ailogo'], // AI logo
    ['ai_commercial_web', 'personbg'], // 人像背景
    ['ai_commercial_web', 'znhz'], // AI试衣
    ['kt', 'kt'], // 抠图
    ['viral_video_web', 'viral_video_generate'], // 爆款视频生成
    ['viral_video_web', 'viral_video_replicate'], // 爆款视频复刻
    ['video_translation_web', 'video_translation'], // ai视频翻译
  ];

  let defaultTab: any = '';

  switch (subscribeType) {
    case SubscribeType.SUBSCRIPTION:
      // 会员场景
      if (vipInfo?.is_vip) {
        // 会员（高级会员/基础会员），默认引导购买高级会员
        defaultTab =
          getIncomeSource() !== 'MTXX-WEB' || isTeamSubscription ? TabType.SUB : TabType.PRO_SUB;
      } else {
        // 非会员高消耗场景，默认引导购买高级会员
        // 非会员非高消耗场景，默认引导购买基础会员
        defaultTab =
          getIncomeSource() === 'MTXX-WEB' &&
          !isTeamSubscription &&
          hightConsumptionModules.some(([m, s]) => m === moduleId && s === source)
            ? TabType.PRO_SUB
            : TabType.SUB;
      }

      break;
    case SubscribeType.MEIDOU:
      // 美豆场景
      if (vipInfo?.is_vip) {
        // web个人版：高级会员默认美豆，标准会员默认高级会员
        // web团队版：会员默认美豆
        // 内嵌场景：会员默认美豆
        defaultTab =
          getIncomeSource() === 'MTXX-WEB' &&
          !isTeamSubscription &&
          !isProVipLevel(vipInfo?.vip_level)
            ? TabType.PRO_SUB
            : TabType.MEIDOU;
      } else {
        // 非会员高消耗场景，默认引导购买高级会员
        // 非会员非高消耗场景，默认引导购买基础会员
        defaultTab =
          getIncomeSource() === 'MTXX-WEB' &&
          !isTeamSubscription &&
          hightConsumptionModules.some(([m, s]) => m === moduleId && s === source)
            ? TabType.PRO_SUB
            : TabType.SUB;
      }
      break;
    case SubscribeType.CONSUMABLE:
      // 抠图场景
      if (vipInfo?.is_vip) {
        defaultTab = TabType.CUTOUT;
      } else {
        defaultTab = TabType.SUB;
      }
      break;
    case SubscribeType.MEIDOU_ONLY:
      // 仅美豆场景，直接展示美豆商品
      defaultTab = TabType.MEIDOU;
      break;
    case SubscribeType.PRO_ONLY:
      // 仅高级会员场景，直接展示高级会员商品
      defaultTab = TabType.PRO_SUB;
      break;
    default:
  }

  // 接口下发的 default_tab 优先级最高（CONSUMABLE 场景忽略；SUBSCRIPTION 场景下若下发 MEIDOU 则忽略；新版 UI 不生效）
  const apiDefaultTab = store.get(apiDefaultTabAtom);
  if (apiDefaultTab && subscribeType !== SubscribeType.CONSUMABLE) {
    if (!(subscribeType === SubscribeType.SUBSCRIPTION && apiDefaultTab === TabType.MEIDOU)) {
      defaultTab = apiDefaultTab;
    }
  }

  // URL 透传 vip_default_meidou=1：仅美豆场景下，当前为会员（标准/高级）时默认展示美豆 tab
  // 目前只有/openClaw 页面中在用
  if (
    subscribeType === SubscribeType.MEIDOU &&
    getUrlParam('vip_default_meidou') === '1' &&
    vipInfo?.is_vip
  ) {
    defaultTab = TabType.MEIDOU;
  }

  if (defaultTab) {
    store.set(curTabAtom, resolveInitialCurTabForNewUi(defaultTab));
  }
};

/**
 * 页面前置初始化逻辑
 */
export const initPage = async () => {
  const isMtxxWeb = getIncomeSource() === 'MTXX-WEB';
  // URL没携带type类型则默认展示vip类型
  const typeP = (getUrlParam('type') as string) || 'vip';

  if (!isMtxxWeb) {
    await initQt();

    if (getUrlParam('client_referer') === 'sub') {
      // PC端订阅登录后回跳到该页面需要触发pc重新发页面进入事件
      track({
        event_id: 'vip_page_enter_new',
        event_name: '页面进入',
        params: {},
      });
    }
  }

  // 获取并缓存gid
  const gid = await getGid();

  // 主题色
  const theme = await getTheme();
  document.documentElement.classList.add(theme);

  if (!isMtxxWeb) {
    const token = getUrlParam('token');
    const code = getUrlParam('code');
    const op_type = getUrlParam('op_type');

    // 3-退出登录, 7-绑定成功, 16-取消绑定
    if (op_type === '3' || op_type === '7' || op_type === '16') {
      if (op_type === '3') await native.logout();
      native.closeVipSubscribeDialog();
      native.closeAccountLoginDialog();
      return;
    }

    console.log('code==========', code);
    if (code || token) {
      // NOTE 登录回跳后cookie会自动种植 __mt_web_access_token__ ，调用fetchUserToken接口时需要cookie上带有 __mt_web_access_token__
      try {
        const tokenRes = await fetchUserToken({
          code,
          token,
          client_id: getClientId(),
        });
        console.log('code to token success==========');
        if (tokenRes?.data?.data?.access_token) {
          setToken(tokenRes.data.data);

          // pc订阅中，支持pc端登录操作。直接关闭弹窗
          if (getUrlParam('state') !== 'sub' && getUrlParam('client_referer') !== 'sub') {
            native.closeVipSubscribeDialog();
            native.closeAccountLoginDialog();
            // setTimeout(() => {
            //   // 不知道干嘛用，从N多年前仓库代码搬迁过来，不敢随意删，就加延时调用（如果直接调用又影响新浏览器执行上面协议）
            //   window.close();
            // }, 300);
            return;
          }
        }
      } catch (e) {
        console.log('code to token fail==========', e);
      }
    } else {
      //  从PC端取token并缓存到本地
      await getTokenAndSet();
      // 子组件异步调用，要先等token拿到之后在加载子组件
      getAccountLoginUrl(typeP);
    }
    // 桌面端，获取审核期间开关状态
    getAuditVersionStatus();
  } else {
    // web端
    // const is_debugger = getUrlParam('debugger'); // debugger为1，代表本地调试模式
    // if (!+is_debugger) {
    //   try {
    //     const res: any = await getAccesTokenByCookie();
    //     const access_token = res?.data?.response?.access_token;
    //     // web订阅要求登录态下才能打开
    //     if (access_token) {
    //       window.localStorage.setItem('access_token', access_token);
    //     } else {
    //       // 未登录，通知上层需要登录
    //       closeVipSubscribeDialog({
    //         needLogin: true,
    //       });
    //     }
    //   } catch (e) {
    //     // 未登录，通知上层需要登录
    //     closeVipSubscribeDialog({
    //       needLogin: true,
    //     });
    //   }
    // }
  }

  // 联想渠道需要开启手动勾选协议
  if (['lxweb1', 'lxweb2'].includes(getChannel() || '')) {
    store.set(auditVersionStatusAtom, true);
  }

  // 先获取 userInfo，AB 实验初始化依赖 uid
  const { access_token, uid } = (await getUserInfo()) || {};

  if (access_token) {
    window.localStorage.setItem('access_token', access_token);
  } else {
    if (isMtxxWeb) {
      closeVipSubscribeDialog({
        needLogin: true,
      });
    }
  }

  // AB 实验须在 loadSubscriptionEntranceEntries 之前初始化（请求需透传 abInfo），
  // 同时依赖 userInfo 中的 uid，因此放在 getUserInfo 之后
  if (!isTeamSubscription && isMtxxWeb && subscribeType !== SubscribeType.CONSUMABLE) {
    await initAbTest();
  }

  // loadSubscriptionEntranceEntries 内会请求入口映射并更新 isFromChannelBusinessAtom / rebateCodeAtom / svipRebateCodeAtom / inviterUidAtom，
  // 须先于 applyMeidouRequireVipSceneOverride / initCurTabValue / checkIsNewUI 结束；
  // 上述判定内的 isInABTesting 还须在 initMtstat 之后，以便进入实验埋点可上报
  const reqList: any = [loadSubscriptionEntranceEntries()];

  // 团队版分佣码走独立接口；个人版分佣码已由 subscribe_entries 下发，不再单独请求
  if (isTeamSubscription) {
    reqList.push(getRebateCode());
  }

  const res = await Promise.all(reqList);

  // 团队版分佣码回写
  if (isTeamSubscription && res?.[1]) {
    store.set(rebateCodeAtom, res[1].data?.data?.rebate_code || '');
    store.set(inviterUidAtom, res[1].data?.data?.inviter_uid || '');
  }

  // 初始化埋点（须先于 isInABTesting：进入实验上报依赖 mtstat）
  await initMtstat(uid);

  // 美豆转会员实验：实验组非会员将有效场景改为购买会员；内部 isInABTesting 会触发进入埋点
  applyMeidouRequireVipSceneOverride();

  // 初始化 curTab（依赖有效 subscribeType；resolveInitialCurTabForNewUi 内也会 isInABTesting）
  initCurTabValue();

  if (store.get(curTabAtom) === TabType.PRO_SUB) {
    applyProPeriodTabOnEnter();
  }

  // 同步更新入口标识（依赖 curTab）
  syncSubEntranceBizCode();

  store.set(isViewNewUiAtom, checkIsNewUI());

  store.set(isInitAtom, true);

  if (typeP === 'svip') {
    getUserBatchSize();
  }

  // 如果携带user_promote_code邀请码字段，则自动打开兑换码页面
  if (getUrlParam('user_promote_code')) {
    store.set(visibleExchangeAtom, true);
  }
};
